import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { UtilsService } from 'core/utils/utils.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AccountsListService } from 'src/app/modules/crm/accounts/services/accounts-list.service';
import { AccountManager } from 'src/app/modules/shared/entities/account-manager.class';
import { AppInfoService } from 'core/app-info/app-info.service';
import { countriesWithNoCurrencyDisplayed } from 'core/app-info/country-specifics.enum';
import { AppStateService } from 'core/app-state/app-state.service';
import { SearchService } from 'core/search/search.service';
import { ActiveTab } from 'src/app/modules/shared/entities/active-tab.enum';
import { StorageValues } from '../../../shared/entities/storage-values.enum';

@Component({
  selector: 'app-quote-advanced-search',
  templateUrl: './quote-advanced-search.component.html',
  styleUrls: ['./quote-advanced-search.component.scss'],
  standalone: false
})
export class QuoteAdvancedSearchComponent implements OnInit {
  newSearchFormFields = {
    accountManager: null,
    customerName: '',
    bpNumber: '',
    contact: '',
    partNumber: '',
    invoiceNumber: '',
    quoteName: '',
    quoteNumber: '',
    dollarAmount: '',
    poNumber: '',
    orderNumber: '',
    lnOrderStatus: null,
    quotesShowFor: null,
    startDate: '',
    endDate: '',
  };

  isMobile: boolean;
  isOnAccountsPage: boolean;
  newSearchForm = this.fb.group(this.newSearchFormFields);
  appStrings: any;
  dsDateFormat = this.appInfoService.dsDateFormat;
  defaultAccountManager: any;
  oldAccountManager: AccountManager;
  clearFiltersNeeded = false;
  mobileAMList: { value: string, label: string }[] = [];
  amList: AccountManager[];
  filteredAMList: AccountManager[];
  lnStatuses: { value: string, label: string }[] = [];
  showForOptions: { value: string, label: string }[] = [];
  cachedSearchValues: any;
  cachedSort: string;
  isInvoiceTab: boolean;
  isLnOrderTab: boolean;
  allAM = {
    name: 'All',
    employeeId: '0'
  };
  allShowFor = 1;
  currencySymbol: string;
  formHasValue = false;
  formHasValueForClear = false;
  activeTab: string;
  isOnline: boolean;
  displayedTextAmount: string;
  countryCode: string;
  displayCurrency: boolean;
  storageValues = StorageValues;

  get dialogTitle(): string {
    const titleMap = {
      [ActiveTab.Orders]: this.appStrings.quotes_searchOrders,
      [ActiveTab.LNOrders]: this.appStrings.quotes_searchLnOrders,
      [ActiveTab.Invoices]: this.appStrings.quotes_searchInvoices
    };

    return titleMap[this.data.activeTab] || this.appStrings.quotes_searchQuotes;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private appInfoService: AppInfoService,
    public accountsListService: AccountsListService,
    private appStateService: AppStateService,
    private searchService: SearchService,
    private utilsService: UtilsService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<QuoteAdvancedSearchComponent>,
    public deviceService: DeviceDetectorService
  ) { }

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobile();
    this.countryCode = this.appInfoService.userData?.UserInfo?.CountryCode || 'US';
    this.displayCurrency = !countriesWithNoCurrencyDisplayed[this.countryCode];
    this.currencySymbol = this.displayCurrency ? this.appInfoService.currencySymbol : '';
    this.appStrings = this.appInfoService.appStrings;
    this.setTabKey();
    this.isOnline = this.appStateService.state.isOnline;
    this.isOnAccountsPage = this.data.isOnAccountsPage;

    this.lnStatuses = this.data.templateValues.lnStatuses;
    this.showForOptions = this.data.templateValues.showForOptions;

    this.amList = this.data.amList;
    this.mobileAMList = this.utilsService.mapObjectToSelect(this.data.amList, 'employeeId', 'name');
    this.filteredAMList = this.amList;
    this.defaultAccountManager = this.allAM.employeeId;
    this.determineAmountTextToDisplay();

    if (this.isOnAccountsPage) {
      this.newSearchForm.patchValue(this.data.accountSearchValues);
      this.newSearchForm.controls.accountManager.setValue(this.data.accountSearchValues?.accountManager?.employeeId);
    } else {
      this.getAndApplyStickyFilters();
    }

    this.setDefaultAccountManager();
    this.setDefaultShowFor();

    // need to listen to value changes on the form to update formHasValue flag
    this.newSearchForm.valueChanges.subscribe(newVal => this.updateFormHasValue(newVal));
    this.updateFormHasValue(this.newSearchForm.value); // update for the initial state
  }

  determineAmountTextToDisplay() {
    if (countriesWithNoCurrencyDisplayed[this.countryCode]) {
      this.displayedTextAmount = this.appStrings.quoteSearch_amount;
      this.displayCurrency = false;
    } else {
      this.displayedTextAmount = this.appStrings.quoteSearch_dollarAmount;
      this.displayCurrency = true;
    }
  }

  /**
   * Updates the status of the formHasValue flag
   */
  private updateFormHasValue(newVal: any) {
    this.formHasValue = false;
    this.formHasValueForClear = false;

    this.checkNonDateFields(newVal);
    this.checkDateFields(newVal);
  }

  private checkNonDateFields(newVal: any) {
    for (const [key, value] of Object.entries(newVal)) {
      if (this.shouldSkipField(key)) {
        continue;
      }
      if (this.hasSignificantValue(key, value)) {
        this.formHasValue = true;
        this.formHasValueForClear = true;
      }
    }
  }

  private shouldSkipField(key: string): boolean {
    return key === 'startDate' || key === 'endDate';
  }

  private hasSignificantValue(key: string, value: any): boolean {
    if (key === 'accountManager') {
      return value && value !== this.defaultAccountManager;
    }
    if (key === 'quotesShowFor' || key === 'ordersShowFor') {
      return value && value !== this.allShowFor;
    }
    return !!value;
  }

  private checkDateFields(newVal: any) {
    const hasStartDate = !!newVal.startDate;
    const hasEndDate = !!newVal.endDate;
    if (hasStartDate && hasEndDate) {
      // Both dates present - valid, enable search and clear
      this.formHasValue = true;
      this.formHasValueForClear = true;
    } else if (hasStartDate !== hasEndDate) {
      // Only one date filled - enable clear button, but search depends on other fields
      this.formHasValueForClear = true;
      // formHasValue remains whatever it was from other fields
    }
  }

  /**
   * Determines the current tab
   */
  setTabKey() {
    if (this.data.sortKey === StorageValues.InvoiceKey) {
      this.isInvoiceTab = true;
      this.activeTab = StorageValues.InvoiceKey;
    } else {
      this.isInvoiceTab = false;
      if (this.data.sortKey === StorageValues.OrderKey) {
        this.activeTab = StorageValues.OrderKey;
      } else {
        if (this.data.sortKey === StorageValues.LnOrderKey) {
          this.isLnOrderTab = true;
          this.activeTab = StorageValues.LnOrderKey;
        } else {
          this.activeTab = StorageValues.QuoteKey;
        }
      }
    }
  }

  /**
   *
   * Filters the AM list
   */
  filterAccountManagerList(searchValue?) {
    if (!searchValue) {
      this.filteredAMList = this.amList;
      return;
    }
    this.filteredAMList = this.searchService.filterResults(searchValue, this.amList, ['name'], ['name']);
  }

  displayAccountManager(accountManager: AccountManager) {
    return accountManager ? accountManager.name : '';
  }


  private getAndApplyStickyFilters() {
    this.cachedSearchValues = sessionStorage.getItem(StorageValues.CachedFilters + this.data.sortKey);
    this.cachedSort = sessionStorage.getItem(StorageValues.CachedSort + this.data.sortKey);

    if (this.cachedSearchValues) {
      this.cachedSearchValues = JSON.parse(this.cachedSearchValues);
      this.clearFiltersNeeded = true;
      this.newSearchForm.patchValue(this.cachedSearchValues);
    }
    if (this.cachedSort) {
      this.cachedSort = JSON.parse(this.cachedSort);
    }
    this.newSearchForm.controls.accountManager.setValue(this.cachedSearchValues?.accountManager?.employeeId);
  }

  private setDefaultAccountManager() {
    if (this.appInfoService.isAccountManager()) {
      this.defaultAccountManager = {
        employeeId: this.appInfoService.userData.UserInfo.EmployeeId,
        name: this.appInfoService.userData.UserInfo.Name
      };
    } else {
      this.defaultAccountManager = this.allAM;
    }
    this.defaultAccountManager = this.defaultAccountManager.employeeId;
    if (!this.newSearchForm.controls.accountManager.value) {
      this.newSearchForm.controls.accountManager.setValue(this.defaultAccountManager);
    }
  }

  private setDefaultShowFor() {
    if (this.newSearchForm.controls.quotesShowFor.value === null ||
      this.newSearchForm.controls.quotesShowFor.value === undefined) {
      this.newSearchForm.controls.quotesShowFor.setValue(this.allShowFor);
    }
  }

  selectedAccountManager(event: MatAutocompleteSelectedEvent) {
    const newAccountManager = event.option.value;
    this.clearFiltersNeeded = true;

    if (!newAccountManager) {
      this.filteredAMList = this.amList;
      return;
    }

    this.filteredAMList = [newAccountManager];
    this.oldAccountManager = newAccountManager;
    this.newSearchForm.controls.accountManager.setValue(newAccountManager);
  }


  /**
   * Clears the value from given control name input
   * @param controlName - the control name for which the value needs to be removed
   */
  clearFilter(controlName) {
    this.newSearchForm.controls[controlName].setValue('');
    this.newSearchForm.markAsDirty();
  }

  /**
   * Clears all values from the form
   */
  clearAllFilters() {
    this.newSearchForm.markAsDirty();
    this.newSearchForm.setValue(this.newSearchFormFields);
    this.filteredAMList = this.amList;
    this.newSearchForm.controls.accountManager.setValue(this.defaultAccountManager);
    this.newSearchForm.controls.quotesShowFor.setValue(this.allShowFor);
  }

  /**
   * Searches from the search textbox
   */
  searchText() {
    if (this.newSearchForm.dirty) {
      const values = { ...this.newSearchForm.value };
      const selectedAM = this.amList.find(am => am.employeeId === values.accountManager);
      if (selectedAM) {
        values.accountManager = {
          employeeId: selectedAM.employeeId,
          name: selectedAM.name
        };
      }
      this.dialogRef.close(values);
    } else {
      this.dialogRef.close();
    }
  }

  closeDialog() {
    // Check if form is dirty but has no values, meaning user cleared everything
    if (!this.formHasValue && this.newSearchForm.dirty) {
      this.dialogRef.close({ clearAllFilters: true });
    } else {
      this.dialogRef.close();
    }
  }

}
